import { useMemo } from 'react'

import { Box, Text } from '@chakra-ui/react'

import { AutoResizeTextarea } from '@/views/components/AutoResizeTextarea/AutoResizeTextarea'
import { MarkdownRenderer } from '@/views/components/MarkdownRenderer/MarkdownRenderer'
import { useVideoPlayer } from '@/views/screens/Interview/components/VideoPlayer/VideoPlayerContext'

export const FullTranscript = ({
  transcript,
  localTranscript,
  setLocalTranscript,
  isEditing,
}: {
  transcript: string
  localTranscript: string
  setLocalTranscript: (transcript: string) => void
  isEditing: boolean
}) => {
  return (
    <>
      <AutoResizeTextarea
        rounded={'1.25rem'}
        flexGrow={1}
        onChange={(event) => {
          event.preventDefault()

          setLocalTranscript(event.target.value)
        }}
        value={transcript}
        variant={'brand-solid-white'}
        display={isEditing ? 'block' : 'none'}
      />
      <Box flexGrow={1} display={isEditing ? 'none' : 'block'}>
        <TranscriptWithTimestamps text={localTranscript} />
      </Box>
    </>
  )
}

const parseTimestampToSeconds = (timestamp: string): number => {
  const parts = timestamp.replace(/[[\]]/g, '').split(':').map(Number)

  if (parts.length === 2) {
    const [minutes, seconds] = parts
    return minutes * 60 + seconds
  }

  if (parts.length === 3) {
    const [hours, minutes, seconds] = parts
    return hours * 3600 + minutes * 60 + seconds
  }

  return 0
}

const TranscriptWithTimestamps = ({ text }: { text: string }) => {
  const videoPlayer = useVideoPlayer()

  const processedContent = useMemo(() => {
    if (!text) {
      return []
    }

    const timestampRegex = /\[(\d{1,2}:\d{2}(?::\d{2})?)\]/g
    const segments: Array<{
      timestamp: string | null
      content: string
    }> = []

    const matches = Array.from(text.matchAll(timestampRegex))

    if (matches.length === 0) {
      return [{ timestamp: null, content: text }]
    }

    for (let i = 0; i < matches.length; i++) {
      const match = matches[i]
      const nextMatch = matches[i + 1]

      if (match.index === undefined) {
        continue
      }

      const startIndex = match.index + match[0].length
      const endIndex =
        nextMatch && nextMatch.index !== undefined
          ? nextMatch.index
          : text.length

      const content = text.substring(startIndex, endIndex)

      segments.push({
        timestamp: match[0],
        content: match[0] + content,
      })
    }

    const firstTimestampIndex = matches[0].index
    if (firstTimestampIndex !== undefined && firstTimestampIndex > 0) {
      segments.unshift({
        timestamp: null,
        content: text.substring(0, firstTimestampIndex),
      })
    }

    return segments
  }, [text])

  const handleSegmentClick = (timestamp: string) => {
    if (videoPlayer) {
      const seconds = parseTimestampToSeconds(timestamp)
      videoPlayer.seekTo(seconds)
    }
  }

  if (!videoPlayer) {
    return <MarkdownRenderer text={text} />
  }

  return (
    <>
      {processedContent.map((segment, index) => {
        if (segment.timestamp) {
          const timestamp = segment.timestamp
          return (
            <Text
              key={index}
              as="span"
              cursor="pointer"
              borderRadius="0.375rem"
              px={1}
              transition="background-color 0.15s ease-in-out"
              _hover={{
                bg: '#E0E0E0',
              }}
              onClick={() => handleSegmentClick(timestamp)}
              whiteSpace="pre-wrap"
            >
              {segment.content}
            </Text>
          )
        }

        return (
          <Text key={index} as="span" whiteSpace="pre-wrap">
            {segment.content}
          </Text>
        )
      })}
    </>
  )
}
